import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import {
  countFailedInvoices,
  countPendingInvoices,
  discardFailedInvoices,
  retryFailedInvoices,
} from '~/offline/draftDb';
import { drainPendingInvoices } from '~/offline/drain';

export interface NetworkStatus {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  /** Emissões que esgotaram as tentativas e esperam ação do usuário. */
  failedCount: number;
  /** Recoloca as falhas na fila e tenta drenar de novo. */
  retryFailed(): Promise<void>;
  /** Descarta as falhas — o usuário optou por não reenviar. */
  discardFailed(): Promise<void>;
}

/**
 * Expõe status de rede + fila offline + dispara drain quando a rede volta.
 *
 * Refresca o contador de pendentes:
 *  - na montagem,
 *  - quando a janela ganha foco (usuário pode ter emitido offline em outra aba),
 *  - quando um drain termina.
 */
export function useNetworkStatus(): NetworkStatus {
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [failedCount, setFailedCount] = useState<number>(0);

  const retryFailed = useCallback(async (): Promise<void> => {
    await retryFailedInvoices();
    await drainPendingInvoices(queryClient);
    setPendingCount(await countPendingInvoices());
    setFailedCount(await countFailedInvoices());
  }, [queryClient]);

  const discardFailed = useCallback(async (): Promise<void> => {
    await discardFailedInvoices();
    setFailedCount(await countFailedInvoices());
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function refreshCount(): Promise<void> {
      try {
        const [pending, failed] = await Promise.all([
          countPendingInvoices(),
          countFailedInvoices(),
        ]);
        if (!cancelled) {
          setPendingCount(pending);
          setFailedCount(failed);
        }
      } catch {
        /* tolerante a Dexie ainda não pronto */
      }
    }

    async function runDrain(): Promise<void> {
      setIsSyncing(true);
      try {
        await drainPendingInvoices(queryClient);
      } finally {
        if (!cancelled) {
          setIsSyncing(false);
          void refreshCount();
        }
      }
    }

    function handleOnline(): void {
      setIsOnline(true);
      void runDrain();
    }
    function handleOffline(): void {
      setIsOnline(false);
    }
    function handleFocus(): void {
      void refreshCount();
      if (navigator.onLine) void runDrain();
    }

    void refreshCount();
    if (navigator.onLine) void runDrain();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('focus', handleFocus);

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('focus', handleFocus);
    };
  }, [queryClient]);

  return { isOnline, isSyncing, pendingCount, failedCount, retryFailed, discardFailed };
}
